import React, { useState, useEffect, useRef } from 'react'
import { employeeService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatCurrency } from '../utils/currency'
import { formatDate } from '../utils/dateFormat'
import useModalFeedback from '../hooks/useModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import CollapsibleFilters from '../components/CollapsibleFilters'

export default function Employees() {
  const { showMessage, showConfirm, modalElement } = useModalFeedback()
  const { isViewer, canMutate } = useRolePermissions()
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    passport_ic_no: '',
    email: '',
    phone_number: '',
    age: '',
    nationality: '',
    position: 'Worker',
    hour_rate: '',
    weekend_rate: '',
    public_holiday_rate: '',
    daily_salary: '',
    monthly_salary: '',
    ot_rate: '',
    status: 'Active',
    calculation: '',
    from_date: '',
    to_date: ''
  })
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState({
    employeeNos: [],
    names: [],
    idPassports: [],
    positions: [],
    statuses: []
  })
  const [filterSearches, setFilterSearches] = useState({
    employeeNo: '',
    name: '',
    idPassport: '',
    position: '',
    status: ''
  })
  const [openDropdowns, setOpenDropdowns] = useState({
    employeeNo: false,
    name: false,
    idPassport: false,
    position: false,
    status: false
  })
  const [employeePage, setEmployeePage] = useState(1)
  const { containerRef: employeeTableRef, rowsPerPage: employeeRowsPerPage } = useResponsivePageSize({ reservedHeight: 560, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })
  const filterContainerRef = useRef(null)

  // Fetch employees on mount
  useEffect(() => {
    fetchEmployees()
  }, [])

  // Refresh data when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      fetchEmployees()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  // Handle clicks outside filter dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
        setOpenDropdowns({
          employeeNo: false,
          name: false,
          idPassport: false,
          position: false,
          status: false
        })
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  
  const fetchEmployees = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await employeeService.getAll()
      setEmployees(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch employees:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch employees')
    } finally {
      setLoading(false)
    }
  }

  const generateEmployeeNumber = (employeeId) => {
    return `EMP-${String(employeeId).padStart(5, '0')}`
  }

  const getUniqueEmployeeNos = () => {
    const unique = [...new Set(employees.map(e => generateEmployeeNumber(e.employee_id)))].sort()
    return unique.filter(no => no.toLowerCase().includes(filterSearches.employeeNo.toLowerCase()))
  }

  const getUniqueNames = () => {
    // Show only names for selected employee nos
    const filteredByEmpNo = filters.employeeNos.length > 0 
      ? employees.filter(e => filters.employeeNos.includes(generateEmployeeNumber(e.employee_id)))
      : employees
    const unique = [...new Set(filteredByEmpNo.map(e => e.name))].filter(Boolean).sort()
    return unique.filter(name => name.toLowerCase().includes(filterSearches.name.toLowerCase()))
  }

  const getUniqueIdPassports = () => {
    // Show only ID/Passports for selected employee nos and names
    const filteredByEmpNo = filters.employeeNos.length > 0 
      ? employees.filter(e => filters.employeeNos.includes(generateEmployeeNumber(e.employee_id)))
      : employees
    const filteredByName = filters.names.length > 0 
      ? filteredByEmpNo.filter(e => filters.names.includes(e.name))
      : filteredByEmpNo
    const unique = [...new Set(filteredByName.map(e => e.passport_ic_no))].filter(Boolean).sort()
    return unique.filter(id => id.toLowerCase().includes(filterSearches.idPassport.toLowerCase()))
  }

  const getUniquePositions = () => {
    // Show only positions for selected employee nos, names, and IDs
    const filteredByEmpNo = filters.employeeNos.length > 0 
      ? employees.filter(e => filters.employeeNos.includes(generateEmployeeNumber(e.employee_id)))
      : employees
    const filteredByName = filters.names.length > 0 
      ? filteredByEmpNo.filter(e => filters.names.includes(e.name))
      : filteredByEmpNo
    const filteredById = filters.idPassports.length > 0 
      ? filteredByName.filter(e => filters.idPassports.includes(e.passport_ic_no))
      : filteredByName
    const unique = [...new Set(filteredById.map(e => e.position))].filter(Boolean).sort()
    return unique.filter(pos => pos.toLowerCase().includes(filterSearches.position.toLowerCase()))
  }

  const getUniqueStatuses = () => {
    // Show only statuses for selected filters
    const filteredByEmpNo = filters.employeeNos.length > 0 
      ? employees.filter(e => filters.employeeNos.includes(generateEmployeeNumber(e.employee_id)))
      : employees
    const filteredByName = filters.names.length > 0 
      ? filteredByEmpNo.filter(e => filters.names.includes(e.name))
      : filteredByEmpNo
    const filteredById = filters.idPassports.length > 0 
      ? filteredByName.filter(e => filters.idPassports.includes(e.passport_ic_no))
      : filteredByName
    const filteredByPosition = filters.positions.length > 0 
      ? filteredById.filter(e => filters.positions.includes(e.position))
      : filteredById
    const statuses = [...new Set(filteredByPosition.map(e => e.status))].filter(Boolean)
    return ['Active', 'Inactive'].filter(status => 
      statuses.includes(status) && status.toLowerCase().includes(filterSearches.status.toLowerCase())
    )
  }

  const getFilteredEmployees = () => {
    return employees.filter(emp => {
      const empNo = generateEmployeeNumber(emp.employee_id)
      const matchEmpNo = filters.employeeNos.length === 0 || filters.employeeNos.includes(empNo)
      const matchName = filters.names.length === 0 || filters.names.includes(emp.name)
      const matchId = filters.idPassports.length === 0 || filters.idPassports.includes(emp.passport_ic_no)
      const matchPosition = filters.positions.length === 0 || filters.positions.includes(emp.position)
      const matchStatus = filters.statuses.length === 0 || filters.statuses.includes(emp.status)
      
      return matchEmpNo && matchName && matchId && matchPosition && matchStatus
    })
  }

  const filteredEmployees = getFilteredEmployees()
  const totalEmployeePages = Math.max(1, Math.ceil(filteredEmployees.length / employeeRowsPerPage))
  const paginatedEmployees = filteredEmployees.slice((employeePage - 1) * employeeRowsPerPage, employeePage * employeeRowsPerPage)

  useEffect(() => {
    setEmployeePage(1)
  }, [filters, searchText, employeeRowsPerPage])

  useEffect(() => {
    setEmployeePage((prev) => Math.min(prev, totalEmployeePages))
  }, [totalEmployeePages])

  const handleFilterCheckChange = (type, value, checked) => {
    setFilters(prev => {
      const current = prev[type] || []
      if (checked) {
        return { ...prev, [type]: [...current, value] }
      } else {
        return { ...prev, [type]: current.filter(v => v !== value) }
      }
    })
  }

  const handleFilterSearchChange = (field, value) => {
    setFilterSearches(prev => ({ ...prev, [field]: value }))
  }

  const toggleDropdown = (field) => {
    setOpenDropdowns(prev => ({
      employeeNo: field === 'employeeNo' ? !prev.employeeNo : false,
      name: field === 'name' ? !prev.name : false,
      idPassport: field === 'idPassport' ? !prev.idPassport : false,
      position: field === 'position' ? !prev.position : false,
      status: field === 'status' ? !prev.status : false
    }))
  }

  const clearAllFilters = () => {
    setFilters({ employeeNos: [], names: [], idPassports: [], positions: [], statuses: [] })
    setFilterSearches({ employeeNo: '', name: '', idPassport: '', position: '', status: '' })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const openCreateModal = () => {
    setEditingId(null)
    setFormData({
      employee_id: '',
      name: '',
      passport_ic_no: '',
      email: '',
      phone_number: '',
      age: '',
      nationality: '',
      position: 'Worker',
      hour_rate: '',
      weekend_rate: '',
      public_holiday_rate: '',
      daily_salary: '',
      monthly_salary: '',
      ot_rate: '',
      team: '',
      status: 'Active',
      calculation: '',
      from_date: '',
      to_date: ''
    })
    setShowModal(true)
  }

  const openEditModal = (employee) => {
    setEditingId(employee.employee_id)
    setFormData({
      employee_id: employee.employee_id || '',
      name: employee.name || '',
      passport_ic_no: employee.passport_ic_no || '',
      email: employee.email || '',
      phone_number: employee.phone_number || '',
      age: employee.age || '',
      nationality: employee.nationality || '',
      position: employee.position || 'Worker',
      hour_rate: employee.hour_rate || '',
      weekend_rate: employee.weekend_rate || '',
      public_holiday_rate: employee.public_holiday_rate || '',
      daily_salary: employee.daily_salary || employee.salary || '',
      monthly_salary: employee.monthly_salary || '',
      ot_rate: employee.ot_rate || '',
      team: employee.team || '',
      status: employee.status || 'Active',
      calculation: employee.calculation || '',
      from_date: employee.from_date ? employee.from_date.split('T')[0] : '',
      to_date: employee.to_date ? employee.to_date.split('T')[0] : ''
    })
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)

      // Validate required fields
      if (!formData.name || !formData.from_date) {
        showMessage('Please fill in all required fields (Name, Joined Date)', 'Validation')
        setLoading(false)
        return
      }

      // If status is Inactive, to_date is required
      if (formData.status === 'Inactive' && !formData.to_date) {
        showMessage('Please enter end date for inactive employees', 'Validation')
        setLoading(false)
        return
      }

      if (editingId) {
        // Update existing employee
        await employeeService.update(editingId, formData)
      } else {
        // Create new employee
        await employeeService.create(formData)
      }

      setShowModal(false)
      setEditingId(null)
      setFormData({
        name: '',
        passport_ic_no: '',
        email: '',
        phone_number: '',
        age: '',
        nationality: '',
        position: 'Worker',
        hour_rate: '',
        weekend_rate: '',
        public_holiday_rate: '',
        daily_salary: '',
        monthly_salary: '',
        ot_rate: '',
        team: '',
        status: 'Active',
        from_date: '',
        to_date: ''
      })

      showMessage(editingId ? 'Employee updated successfully!' : 'Employee created successfully!', 'Success')
      await fetchEmployees()
    } catch (error) {
      console.error('Failed to save employee:', error)
      showMessage(error.response?.data?.message || 'Failed to save employee', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteEmployee = async (id) => {
    const confirmed = await showConfirm('Are you sure you want to delete this employee? This action cannot be undone.')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await employeeService.delete(id)
      showMessage('Employee deleted successfully!', 'Success')
      await fetchEmployees()
    } catch (error) {
      console.error('Failed to delete employee:', error)
      showMessage(error.response?.data?.message || 'Failed to delete employee', 'Error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Employees</h1>
        {canMutate && (
          <button 
            onClick={openCreateModal}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 sm:w-auto"
          >
            + Add Employee
          </button>
        )}
      </div>
      {/* Filter Section */}
        <CollapsibleFilters>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Employee No. {filters.employeeNos.length > 0 && <span className="text-blue-600 font-semibold">({filters.employeeNos.length})</span>}</label>
              <button
                type="button"
                onClick={() => toggleDropdown('employeeNo')}
                className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className="truncate">{filters.employeeNos.length === 0 ? 'Select...' : filters.employeeNos.join(', ')}</span>
                <span>{openDropdowns.employeeNo ? '▲' : '▼'}</span>
              </button>
              {openDropdowns.employeeNo && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterSearches.employeeNo}
                    onChange={(e) => handleFilterSearchChange('employeeNo', e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                  />
                  <div className="p-2 space-y-1">
                    {getUniqueEmployeeNos().length > 0 ? (
                      getUniqueEmployeeNos().map(empNo => (
                        <label key={empNo} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={filters.employeeNos.includes(empNo)}
                            onChange={(e) => handleFilterCheckChange('employeeNos', empNo, e.target.checked)}
                            className="mr-2 w-5 h-5 rounded"
                          />
                          <span className="text-sm font-mono font-semibold text-blue-600">{empNo}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-gray-500">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Name Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Name {filters.names.length > 0 && <span className="text-blue-600 font-semibold">({filters.names.length})</span>}</label>
              <button
                type="button"
                onClick={() => toggleDropdown('name')}
                className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className="truncate">{filters.names.length === 0 ? 'Select...' : filters.names.join(', ')}</span>
                <span>{openDropdowns.name ? '▲' : '▼'}</span>
              </button>
              {openDropdowns.name && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterSearches.name}
                    onChange={(e) => handleFilterSearchChange('name', e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                  />
                  <div className="p-2 space-y-1">
                    {getUniqueNames().length > 0 ? (
                      getUniqueNames().map(name => (
                        <label key={name} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={filters.names.includes(name)}
                            onChange={(e) => handleFilterCheckChange('names', name, e.target.checked)}
                            className="mr-2 w-5 h-5 rounded"
                          />
                          <span className="text-sm">{name}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-gray-500">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ID/Passport Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by ID/Passport {filters.idPassports.length > 0 && <span className="text-blue-600 font-semibold">({filters.idPassports.length})</span>}</label>
              <button
                type="button"
                onClick={() => toggleDropdown('idPassport')}
                className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className="truncate">{filters.idPassports.length === 0 ? 'Select...' : filters.idPassports.join(', ')}</span>
                <span>{openDropdowns.idPassport ? '▲' : '▼'}</span>
              </button>
              {openDropdowns.idPassport && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterSearches.idPassport}
                    onChange={(e) => handleFilterSearchChange('idPassport', e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                  />
                  <div className="p-2 space-y-1">
                    {getUniqueIdPassports().length > 0 ? (
                      getUniqueIdPassports().map(id => (
                        <label key={id} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={filters.idPassports.includes(id)}
                            onChange={(e) => handleFilterCheckChange('idPassports', id, e.target.checked)}
                            className="mr-2 w-5 h-5 rounded"
                          />
                          <span className="text-sm font-mono">{id}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-gray-500">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Position Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Position {filters.positions.length > 0 && <span className="text-blue-600 font-semibold">({filters.positions.length})</span>}</label>
              <button
                type="button"
                onClick={() => toggleDropdown('position')}
                className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className="truncate">{filters.positions.length === 0 ? 'Select...' : filters.positions.join(', ')}</span>
                <span>{openDropdowns.position ? '▲' : '▼'}</span>
              </button>
              {openDropdowns.position && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterSearches.position}
                    onChange={(e) => handleFilterSearchChange('position', e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                  />
                  <div className="p-2 space-y-1">
                    {getUniquePositions().length > 0 ? (
                      getUniquePositions().map(position => (
                        <label key={position} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={filters.positions.includes(position)}
                            onChange={(e) => handleFilterCheckChange('positions', position, e.target.checked)}
                            className="mr-2 w-5 h-5 rounded"
                          />
                          <span className="text-sm">{position}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-gray-500">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status {filters.statuses.length > 0 && <span className="text-blue-600 font-semibold">({filters.statuses.length})</span>}</label>
              <button
                type="button"
                onClick={() => toggleDropdown('status')}
                className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className="truncate">{filters.statuses.length === 0 ? 'Select...' : filters.statuses.join(', ')}</span>
                <span>{openDropdowns.status ? '▲' : '▼'}</span>
              </button>
              {openDropdowns.status && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterSearches.status}
                    onChange={(e) => handleFilterSearchChange('status', e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                  />
                  <div className="p-2 space-y-1">
                    {getUniqueStatuses().map(status => (
                      <label key={status} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                        <input
                          type="checkbox"
                          checked={filters.statuses.includes(status)}
                          onChange={(e) => handleFilterCheckChange('statuses', status, e.target.checked)}
                          className="mr-2 w-5 h-5 rounded"
                        />
                        <span className={`text-sm font-medium ${
                          status === 'Active' ? 'text-green-700' : 'text-red-700'
                        }`}>{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-end">
            <button
              onClick={clearAllFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
          </div>
        </CollapsibleFilters>

      {/* Employees Table */}
      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchEmployees}
        loadingText="Loading employee records..."
        containerRef={employeeTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Employee No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">ID / Passport</th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Position</th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Team</th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Hour Rate</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Weekend Rate</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Public Holiday Rate</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Daily Salary</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Monthly Salary</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">OT Rate</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Joined Date</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">End Date</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap text-center">Status</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap text-center">Calculation</th>
              {!isViewer && <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap text-center">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '14' : '15'} className="px-3 py-2 text-center text-gray-500">
                  {employees.length === 0 ? 'No employees yet. Add your first employee!' : 'No employees match the selected filters.'}
                </td>
              </tr>
            ) : (
              paginatedEmployees.map((employee) => (
                <tr 
                  key={employee.employee_id} 
                  className="border-b hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => openEditModal(employee)}
                >
                  <td className="px-3 py-2 text-sm font-semibold text-blue-600 whitespace-nowrap">{generateEmployeeNumber(employee.employee_id)}</td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap"><TruncatedText text={employee.name} /></td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{employee.passport_ic_no}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{employee.position}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{employee.team || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatCurrency(employee.hour_rate)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatCurrency(employee.weekend_rate)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatCurrency(employee.public_holiday_rate)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatCurrency(employee.daily_salary || employee.salary)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatCurrency(employee.monthly_salary)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {typeof employee.ot_rate === 'number' ? Number(employee.ot_rate).toFixed(2) : (employee.ot_rate || '-')}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {employee.from_date ? formatDate(employee.from_date) : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {employee.to_date ? formatDate(employee.to_date) : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      employee.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {employee.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      employee.calculation === 'Month'
                        ? 'bg-blue-100 text-blue-800'
                        : employee.calculation === 'Day'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {employee.calculation || '-'}
                    </span>
                  </td>
                  {!isViewer && (
                    <td className="px-3 py-2 text-sm whitespace-nowrap text-center space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openEditModal(employee)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteEmployee(employee.employee_id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          currentPage={employeePage}
          totalPages={totalEmployeePages}
          totalItems={filteredEmployees.length}
          rowsPerPage={employeeRowsPerPage}
          onPrevious={() => setEmployeePage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setEmployeePage((prev) => Math.min(prev + 1, totalEmployeePages))}
        />
      </TableStateWrapper>

      {/* Create/Edit Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="modal-panel-standard bg-white max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">
                {editingId ? `Edit Employee No. ${formData.employee_id}` : 'Add New Employee'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., John Doe"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID / Passport
                    </label>
                    <input
                      type="text"
                      name="passport_ic_no"
                      value={formData.passport_ic_no}
                      onChange={handleInputChange}
                      placeholder="e.g., ABC123456"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="e.g., john@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      placeholder="e.g., 0123456789"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Daily Salary (RM)
                    </label>
                    <input
                      type="number"
                      name="daily_salary"
                      value={formData.daily_salary}
                      onChange={handleInputChange}
                      placeholder="e.g., 120.00"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Salary (RM)
                    </label>
                    <input
                      type="number"
                      name="monthly_salary"
                      value={formData.monthly_salary}
                      onChange={handleInputChange}
                      placeholder="e.g., 2500.00"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OT Rate
                    </label>
                    <input
                      type="number"
                      name="ot_rate"
                      value={formData.ot_rate}
                      onChange={handleInputChange}
                      placeholder="e.g., 1.50"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      placeholder="e.g., 35"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      max="100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nationality
                    </label>
                    <input
                      type="text"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleInputChange}
                      placeholder="e.g., Malaysian"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                    >
                      <option value="Site Leader">Site Leader</option>
                      <option value="Worker">Worker</option>
                      <option value="Driver">Driver</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                    <input
                      type="text"
                      name="team"
                      value={formData.team}
                      onChange={handleInputChange}
                      placeholder="e.g., Anarul"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hour Rate (RM)
                    </label>
                    <input
                      type="number"
                      name="hour_rate"
                      value={formData.hour_rate}
                      onChange={handleInputChange}
                      placeholder="e.g., 12.50"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weekend Rate (RM)
                    </label>
                    <input
                      type="number"
                      name="weekend_rate"
                      value={formData.weekend_rate}
                      onChange={handleInputChange}
                      placeholder="e.g., 18.00"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Public Holiday Rate (RM)
                    </label>
                    <input
                      type="number"
                      name="public_holiday_rate"
                      value={formData.public_holiday_rate}
                      onChange={handleInputChange}
                      placeholder="e.g., 20.00"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                {/* Dates Row */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Joined Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    name="from_date"
                    value={formData.from_date}
                    onChange={handleInputChange}
                    className="modal-date-input w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date {formData.status === 'Inactive' && <span className="text-red-600">*</span>}
                  </label>
                  <input
                    type="date"
                    name="to_date"
                    value={formData.to_date}
                    onChange={handleInputChange}
                    disabled={formData.status === 'Active'}
                    className={`modal-date-input w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                      formData.status === 'Active' ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calculation
                    </label>
                    <select
                      name="calculation"
                      value={formData.calculation}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">Auto (based on employment duration)</option>
                      <option value="Month">Month</option>
                      <option value="Day">Day</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty for auto-detection: Month if employed &gt;30 days, otherwise Day
                    </p>
                  </div>
              </div>

              <div className="border-t pt-6 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !canMutate}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : editingId ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {modalElement}
    </div>
  )
}
